import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageSquarePlus, Sparkles } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { useNoIndex } from "@/lib/noIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = [["it", "Italiano"], ["en", "English"], ["fr", "Français"]];
const draftQuestion = (q) => `${q.charAt(0).toUpperCase()}${q.slice(1)}: cosa offre Emma Industrie?`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</span>
    {children}
  </label>
);

const FaqForm = ({ token, draft }) => {
  const [form, setForm] = useState({ question: draft.faq?.question ?? draftQuestion(draft.q), answer: draft.faq?.answer ?? "", lang: draft.faq?.lang ?? draft.lang });
  const [state, setState] = useState(draft.faq ? "published" : "idle");
  const [drafting, setDrafting] = useState(false);
  const suggest = async (question = form.question, lang = form.lang) => {
    setDrafting(true);
    try {
      const r = await axios.post(`${API}/faq-draft/${token}/suggest`, { question, lang });
      if (r.data?.answer) setForm((f) => ({ ...f, answer: r.data.answer }));
    } catch {
      toast.error("Bozza non disponibile ora: scrivi la risposta o riprova tra poco.");
    } finally {
      setDrafting(false);
    }
  };
  useEffect(() => {
    if (!draft.faq) suggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      await axios.post(`${API}/faq-draft/${token}`, form);
      setState("published");
      toast.success("Domanda frequente pubblicata sul sito.");
    } catch {
      setState("idle");
      toast.error("Controlla i campi: domanda almeno 5 caratteri, risposta almeno 10.");
    }
  };
  return (
    <form onSubmit={submit} className="mt-10 grid gap-6" data-testid="faq-draft-form">
      <Field label="Domanda">
        <input className="field" required minLength={5} maxLength={200} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-draft-question" />
      </Field>
      <Field label="Risposta">
        <textarea className="field min-h-[180px]" required minLength={10} maxLength={3000} value={form.answer} disabled={drafting} placeholder={drafting ? "Sto preparando una bozza di risposta…" : "Scrivi la risposta come la leggerà il visitatore."} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-draft-answer" />
      </Field>
      <div className="-mt-3 flex flex-wrap items-center gap-3 text-sm text-[#CFC8BC]">
        <button type="button" onClick={() => suggest()} disabled={drafting} className="btn-ghost disabled:opacity-60" data-testid="faq-draft-suggest">
          {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {form.answer ? "Rigenera la bozza" : "Proponi una bozza"}
        </button>
        <span data-testid="faq-draft-ai-note">Bozza scritta dall'AI con i soli dati del sito: controllala e completa le parti [da completare] prima di pubblicare.</span>
      </div>
      <Field label="Lingua del sito in cui compare">
        <select className="field" value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} data-testid="faq-draft-lang">
          {LANGS.map(([v, l]) => <option key={v} value={v} className="bg-[#1B1816]">{l}</option>)}
        </select>
      </Field>
      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" disabled={state === "busy" || drafting} className="btn-amber disabled:opacity-60" data-testid="faq-draft-submit">
          {state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />} {state === "published" ? "Aggiorna la domanda" : "Pubblica sul sito"}
        </button>
        {state === "published" && <span className="flex items-center gap-2 text-sm text-[#A8C98A]" data-testid="faq-draft-published"><CheckCircle2 className="h-4 w-4" /> Pubblicata: è nelle domande frequenti della Home e nella ricerca.</span>}
      </div>
    </form>
  );
};

export default function FaqDraft() {
  const { token } = useParams();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(false);
  useNoIndex();
  useEffect(() => {
    axios.get(`${API}/faq-draft/${token}`).then((r) => setDraft(r.data)).catch(() => setError(true));
  }, [token]);
  return (
    <div className="min-h-screen bg-[#121110] text-[#F3EFE6]" data-testid="faq-draft-page">
      <Nav />
      <main className="mx-auto max-w-3xl px-5 pb-28 pt-40 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">Area riservata · Nuova domanda frequente</p>
        {error && <p className="mt-8 text-lg text-[#CFC8BC]" data-testid="faq-draft-invalid">Link non valido. Usa il pulsante ricevuto nell'email di avviso.</p>}
        {!draft && !error && <Loader2 className="mt-10 h-6 w-6 animate-spin text-[#D98A52]" />}
        {draft && <>
          <h1 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">«{draft.q}»</h1>
          <p className="mt-6 text-base leading-relaxed text-[#D6D0C4] sm:text-lg">I visitatori hanno cercato questa parola senza trovare risultati. Scrivi la risposta: una volta pubblicata compare nelle domande frequenti della Home e la ricerca del sito la trova.</p>
          <FaqForm token={token} draft={draft} />
        </>}
      </main>
      <Footer />
    </div>
  );
}
